import React, { useEffect, useState } from 'react';
// import AdminService from '../../../services/admin.service';
import Select from 'react-select';
import { Table, Card, Row, Col, Form } from 'react-bootstrap';
import TablePaginationComponent from '../../component/table/TablePagination';
import { ClientService } from '../../component/services/client.service';
import moment from 'moment';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faUser, faClock, faToggleOn, faToggleOff, faLifeRing, faBookOpen, faUserAlt, faUserCheck, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { UsersCard } from '../../component/Card/Statistics.card';
import CetLoader from '../../component/Loader/CetLoader';
import DonutChart, { ChartDataItem } from '../../component/Charts/DonutChart';
import StatsCard from '../../component/Cards/StatsCard';
import StatsCardWithProgress from '../../component/Cards/StatsCardWithProgress';
import AttendanceOverview from './AttendanceOverview';
import BioMetricOverview from './BioMetricOverview';
import CityWiseOverview from './CityWiseOverview';

interface Props {
    showAttendanceOverview?: boolean
}

export default function VenueWiseReport({ showAttendanceOverview = true}: Props) {
    const [selectedValue, setSelectedValue] = useState<any>()
    const [shifts, setShifts] = useState<any>()
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<any>(null);
    const [chartData, setChartData] = useState<ChartDataItem[]>([]);
    const [lastSynced, setLastSynced] = useState<Date | null>(null);
    const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
    const [nextRefreshIn, setNextRefreshIn] = useState<number>(120); // seconds
    const [biometricStats, setBiometricStats] = useState<any>(null);
    const [cityWiseData, setCityWiseData] = useState<any[]>([]);


    const getData = async () => {
        if (!selectedValue) {
            toast.error("Please select a shift");
            return;
        }

        setLoading(true);
        try {
            let payload = {};
            const res = await ClientService.getVenueWiseAttendanceStats(selectedValue, payload);
            if (res.status === 200) {
                setData(res.data?.data || {});

                const totalCount = res.data?.data?.total_count || 0;
                const presentCount = res.data?.data?.present_count || 0;
                const absentCount = totalCount - presentCount;

                const newChartData: ChartDataItem[] = [
                    {
                        category: "Present",
                        value: presentCount,
                        fill: "#004A2F"
                    },
                    {
                        category: "Absent",
                        value: absentCount,
                        fill: "#6C946F"
                    }
                ];

                setChartData(newChartData);
                setLastSynced(new Date());
                setNextRefreshIn(120); // Reset countdown
                setBiometricStats(res.data?.data?.biometricStats || null);
                setCityWiseData(res.data?.data?.cityWiseStats || []);
            }
        } catch (error) {
            console.error('Error fetching attendance stats:', error);
            toast.error('Failed to fetch attendance data');
        } finally {
            setLoading(false);
        }
    };


    const getAllShift = async () => {
        await ClientService.getAllShifts().then(res => {
            if (res.status === 200) {
                setShifts(res.data.shifts.map((data: any) => {
                    return {
                        label: data.shiftName + " - " + moment(data.startTime).format("DD MMM YYYY"),
                        value: data._id,
                        startTime: data.startTime,
                        endTime: data.endTime,
                        biometricStartTime: data.biometricStartTime,
                        biometricEndTime: data.biometriEndTime
                    }
                }))
            }
        }).catch(err => {
            toast.error(err.response.data);
        })
    }

    useEffect(() => {
        getAllShift();
    }, []);


    useEffect(() => {
        if (selectedValue) {
            getData();
        }
    }, [selectedValue]);

    // Auto-refresh functionality
    useEffect(() => {
        let intervalId: NodeJS.Timeout;
        let countdownId: NodeJS.Timeout;

        if (selectedValue && autoRefresh) {
            // Set up interval to refresh data every 2 minutes (120000 ms)
            intervalId = setInterval(() => {
                getData();
            }, 120000);

            // Set up countdown timer that updates every second
            countdownId = setInterval(() => {
                setNextRefreshIn((prev) => {
                    if (prev <= 1) {
                        return 120; // Reset to 120 seconds
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        // Cleanup intervals on component unmount or when dependencies change
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
            if (countdownId) {
                clearInterval(countdownId);
            }
        };
    }, [selectedValue, autoRefresh]);

    // Format countdown time
    const formatCountdown = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div>
            <Row className="mt-2 text-left mb-0">
                <Col md={8}>
                    <h4 className="text-secondary mt-3 fw-bold">Stats</h4>
                </Col>
                <Col md={4}>
                    <Form.Label className="text-muted fw-bold">
                        Select Shift
                    </Form.Label>
                    <Select
                        options={shifts}
                        placeholder="Select Shifts"
                        onChange={(e: any) => setSelectedValue(e?.value)}
                        isClearable
                        value={selectedValue ? shifts.find((shift: any) => shift.value === selectedValue.value) : null}
                    />
                </Col>
            </Row>

            {
                loading ? (
                    <div className="mt-3">
                        <CetLoader />
                    </div>
                ) :

                    <>
                        <div className='row'>
                            <div className="col">
                                <AttendanceOverview
                                    data={data}
                                    chartData={chartData}
                                    lastSynced={lastSynced}
                                    autoRefresh={autoRefresh}
                                    setAutoRefresh={setAutoRefresh}
                                    nextRefreshIn={nextRefreshIn}
                                    loading={loading}
                                    getData={getData}
                                    setNextRefreshIn={setNextRefreshIn}
                                />
                            </div>
                            {
                                showAttendanceOverview &&
                                <div className="col-md-6">
                                    <BioMetricOverview
                                        data={biometricStats}
                                        loading={loading}
                                        getData={getData}
                                        autoRefresh={autoRefresh}
                                        setAutoRefresh={setAutoRefresh}
                                        lastSynced={lastSynced}
                                        nextRefreshIn={nextRefreshIn}
                                        setNextRefreshIn={setNextRefreshIn}
                                    />

                                </div>
                            }
                        </div>

                        <CityWiseOverview
                            data={cityWiseData}
                            loading={loading}
                            getData={getData}
                            autoRefresh={autoRefresh}
                            setAutoRefresh={setAutoRefresh}
                            setNextRefreshIn={setNextRefreshIn}
                            lastSynced={lastSynced}
                            nextRefreshIn={nextRefreshIn}
                        />

                    </>
            }
        </div>
    );
};

export const NoDataView = ({ message }: { message: string }) => {
    return (
        <div className="text-center py-5">
            <div className="mb-3">
                <FontAwesomeIcon
                    icon={faUser}
                    className="text-muted"
                    style={{ fontSize: '48px', opacity: 0.3 }}
                />
            </div>
            <div className="text-muted" style={{ fontSize: '16px' }}>
                {
                    message || "No data available. Please select a shift to view attendance."
                }
            </div>
        </div>
    );
}